/** The role apps reach Academy through their own origin's authenticated proxy. */
export const ACADEMY_API_BASE = "/api/academy";

export class AcademyApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code = "REQUEST_FAILED",
  ) {
    super(message);
    this.name = "AcademyApiError";
  }
}

export interface AcademyRequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  csrfToken?: string;
}

/** Cookies are the credential. Responses never fall back to local or generated data. */
export async function requestAcademy<T = void>(
  baseUrl: string,
  operation: string,
  options: AcademyRequestOptions = {},
): Promise<T> {
  const origin = typeof location === "undefined" ? "http://localhost" : location.origin;
  let target: URL;
  try {
    const base = new URL(`${baseUrl.replace(/\/+$/, "")}/`, origin);
    target = new URL(operation, base);
    if (
      base.origin !== origin ||
      base.username ||
      base.password ||
      base.search ||
      base.hash ||
      !operation ||
      !/^[a-zA-Z0-9][a-zA-Z0-9/_?=&%.:-]*$/.test(operation) ||
      target.origin !== base.origin ||
      !target.pathname.startsWith(base.pathname) ||
      /(^|\/)\.{1,2}(\/|$)/.test(decodeURIComponent(operation)) ||
      target.hash
    )
      throw new Error("Invalid address");
  } catch {
    throw new AcademyApiError("The Academy service address is invalid.", 0, "INVALID_CONFIGURATION");
  }
  const controller = new AbortController();
  const abort = () => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) abort();
  options.signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(() => controller.abort(), 20_000);
  try {
    const response = await fetch(target, {
      method: options.method ?? (options.body === undefined ? "GET" : "POST"),
      credentials: "include",
      cache: "no-store",
      redirect: "error",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "AcademyWorkspace",
        ...(options.body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(options.csrfToken ? { "X-CSRF-Token": options.csrfToken } : {}),
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
    });
    if (response.ok && response.status === 204) return undefined as T;
    const contentType = response.headers.get("content-type") || "";
    const payload: unknown = /\bapplication\/(?:[\w.-]+\+)?json\b/i.test(contentType)
      ? await response.json().catch(() => null)
      : null;
    if (!response.ok) {
      const record = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
      const message =
        typeof record?.message === "string"
          ? record.message
          : response.status === 401
            ? "Your session has expired. Please sign in again."
            : response.status === 403
              ? "Your account cannot access this workspace."
              : "The Academy service could not complete this request. Please try again.";
      throw new AcademyApiError(
        message,
        response.status,
        typeof record?.code === "string" ? record.code : "REQUEST_FAILED",
      );
    }
    if (payload === null)
      throw new AcademyApiError("The Academy service returned an invalid response.", 502, "INVALID_RESPONSE");
    return payload as T;
  } catch (error) {
    if (options.signal?.aborted || error instanceof AcademyApiError) throw error;
    throw new AcademyApiError(
      controller.signal.aborted
        ? "The Academy service took too long to respond. Please try again."
        : "We could not connect to Academy. Please try again.",
      0,
      controller.signal.aborted ? "TIMEOUT" : "NETWORK_ERROR",
    );
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", abort);
  }
}
